/**
 * CreateMealModal.jsx
 * Modal for creating a custom meal with photo upload for AI ingredient recognition
 * Matches Customer Portal functionality
 */
import React, { useState, useRef } from 'react';
import { X, Camera, Upload, Sparkles, RefreshCw, Plus, Trash2, ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CreateMealModal = ({ isOpen, onClose, onMealCreated, portalToken, customerId }) => {
  const [mealName, setMealName] = useState('');
  const [ingredients, setIngredients] = useState([]);
  const [manualIngredient, setManualIngredient] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [recognizedIngredients, setRecognizedIngredients] = useState([]);
  const [saving, setSaving] = useState(false);
  // "Als Rezept speichern" Toggle — wenn aktiv, wird die Mahlzeit
  // zusätzlich in `user_recipes` als wiederverwendbares Template
  // gespeichert. User kann sie dann später mit 1 Tap aus dem
  // "Meine Rezepte"-Tab erneut loggen.
  const [saveAsRecipe, setSaveAsRecipe] = useState(false);
  const fileInputRef = useRef(null);

  const getDefaultConsumedTimeForMeal = (mealTypeValue) => {
    const mapping = {
      breakfast: '08:00',
      snack_morning: '10:00',
      lunch: '12:30',
      snack_afternoon: '16:00',
      dinner: '19:00',
      snack_evening: '21:00'
    };
    return mapping[mealTypeValue] || '12:30';
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Bild zu gross (max. 10MB)');
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Analyze image with AI
  const analyzeImage = async () => {
    if (!selectedImage || !portalToken) return;
    
    setAnalyzing(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const response = await axios.post(
            `${API}/meal-assistant/analyze-food-photo`,
            {
              customer_id: customerId,
              image_data: reader.result,
              meal_type: 'lunch'
            },
            { 
              headers: { Authorization: `Bearer ${portalToken}` },
              timeout: 60000
            }
          );
          
          if (response.data.components) {
            setRecognizedIngredients(response.data.components);
            // Auto-fill meal name if empty
            if (!mealName && response.data.meal_name) {
              setMealName(response.data.meal_name);
            }
            toast.success('Zutaten erkannt!');
          }
        } catch (err) {
          console.error('Analysis error:', err);
          toast.error('Analyse fehlgeschlagen');
          // Demo fallback
          setRecognizedIngredients([
            { name: 'Hähnchenbrust', grams: 150, calories: 247, protein: 31, carbs: 0, fat: 5 },
            { name: 'Reis', grams: 150, calories: 195, protein: 4, carbs: 43, fat: 0 },
            { name: 'Brokkoli', grams: 100, calories: 35, protein: 3, carbs: 7, fat: 0 },
          ]);
        }
        setAnalyzing(false);
      };
      reader.readAsDataURL(selectedImage);
    } catch (err) {
      toast.error('Fehler beim Lesen des Bildes');
      setAnalyzing(false);
    }
  };

  // Add manual ingredient
  const addManualIngredient = () => {
    if (manualIngredient.trim()) {
      setIngredients([...ingredients, { name: manualIngredient.trim(), grams: 100 }]);
      setManualIngredient('');
    }
  };

  // Add recognized ingredient to meal
  const addRecognizedIngredient = (ingredient) => {
    setIngredients([...ingredients, ingredient]);
    setRecognizedIngredients(recognizedIngredients.filter(i => i.name !== ingredient.name));
  };

  // Remove ingredient
  const removeIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  // Save meal
  const saveMeal = async () => {
    if (!mealName.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    if (ingredients.length === 0) {
      toast.error('Bitte mindestens eine Zutat hinzufügen');
      return;
    }
    
    setSaving(true);
    try {
      // Calculate totals
      const totals = ingredients.reduce((acc, ing) => ({
        calories: acc.calories + (ing.calories || 0),
        protein: acc.protein + (ing.protein || 0),
        carbs: acc.carbs + (ing.carbs || 0),
        fat: acc.fat + (ing.fat || 0),
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      const today = new Date().toISOString().split('T')[0];
      const consumedAt = getDefaultConsumedTimeForMeal('lunch');
      
      await axios.post(
        `${API}/portal/nutrition/entries`,
        {
          date: today,
          meal_type: 'lunch',
          consumed_at: consumedAt,
          food_name: mealName,
          serving_size: 100,
          calories: totals.calories,
          protein: totals.protein,
          carbs: totals.carbs,
          fat: totals.fat,
          notes: `Zutaten: ${ingredients.map(i => i.name).join(', ')}`
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );

      // Optional: Mahlzeit als Rezept speichern (fire-and-forget — wenn
      // der Recipe-Save fehlschlägt, wird der Tagebuch-Save trotzdem
      // committed).
      if (saveAsRecipe) {
        try {
          await axios.post(
            `${API}/portal/nutrition/my-recipes`,
            {
              name: mealName,
              meal_type: 'lunch',
              ingredients: ingredients.map(i => ({
                name: i.name,
                grams: i.grams || 100,
                calories: i.calories || 0,
                protein: i.protein || 0,
                carbs: i.carbs || 0,
                fat: i.fat || 0,
              })),
            },
            { headers: { Authorization: `Bearer ${portalToken}` } }
          );
        } catch (recipeErr) {
          console.warn('[CreateMeal] saveAsRecipe non-blocking', recipeErr);
        }
      }

      toast.success(saveAsRecipe
        ? 'Mahlzeit + Rezept gespeichert!'
        : 'Mahlzeit gespeichert!');
      onMealCreated?.();
      resetAndClose();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Reset and close
  const resetAndClose = () => {
    setMealName('');
    setIngredients([]);
    setManualIngredient('');
    setSelectedImage(null);
    setImagePreview(null);
    setRecognizedIngredients([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/95 z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center gap-3">
          <button onClick={resetAndClose} className="p-2 bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
          <div className="flex-1">
            <h2 className="text-white font-bold">Neues Rezept erstellen</h2>
            <p className="text-zinc-500 text-xs">KI erkennt automatisch die Zutaten</p>
          </div>
          <Sparkles className="w-6 h-6 text-yellow-400" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Meal Name */}
        <div>
          <label className="text-zinc-400 text-xs mb-1 block">Name der Mahlzeit</label>
          <input
            type="text"
            value={mealName}
            onChange={(e) => setMealName(e.target.value)}
            placeholder="z.B. Hähnchen-Reis-Bowl"
            className="w-full px-3 py-2 bg-zinc-800 rounded-lg text-white placeholder-zinc-500"
          />
        </div>

        {/* Photo Upload */}
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <p className="text-zinc-400 text-xs mb-2">📸 Foto der Zutaten machen</p>
          <p className="text-yellow-400/80 text-[10px] mb-3">
            🍴 Tipp: Lege Besteck oder eine Kreditkarte neben das Essen für genauere Portionsgrössen
          </p>
          
          {!imagePreview ? (
            <div className="flex gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-400"
              >
                <Camera className="w-5 h-5" />
                <span className="text-sm">Kamera</span>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-zinc-700 rounded-lg text-zinc-300"
              >
                <ImageIcon className="w-5 h-5" />
                <span className="text-sm">Galerie</span>
              </button>
            </div>
          ) : (
            <div className="relative">
              <img src={imagePreview} alt="Zutaten" className="w-full h-32 object-cover rounded-lg" />
              <button
                onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                className="absolute top-2 right-2 p-1 bg-black/50 rounded-full"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              {!analyzing && recognizedIngredients.length === 0 && (
                <button
                  onClick={analyzeImage}
                  className="mt-2 w-full py-2 bg-yellow-500 rounded-lg text-black font-medium flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Mit KI analysieren
                </button>
              )}
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.gif"
            onChange={handleImageSelect}
            className="hidden"
          />
        </div>

        {/* Analyzing */}
        {analyzing && (
          <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
            <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-2" />
            <p className="text-white text-sm">Analysiere Zutaten...</p>
          </div>
        )}

        {/* Recognized Ingredients */}
        {recognizedIngredients.length > 0 && (
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <p className="text-zinc-400 text-xs mb-2">Erkannte Zutaten (tippen zum Hinzufügen)</p>
            <div className="space-y-2">
              {recognizedIngredients.map((ing, i) => (
                <button
                  key={i}
                  onClick={() => addRecognizedIngredient(ing)}
                  className="w-full flex items-center justify-between p-2 bg-green-500/10 border border-green-500/30 rounded-lg"
                >
                  <span className="text-white text-sm">{ing.name} ({ing.grams}g)</span>
                  <Plus className="w-4 h-4 text-green-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Manual Input */}
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <p className="text-zinc-400 text-xs mb-2">Oder manuell suchen</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualIngredient}
              onChange={(e) => setManualIngredient(e.target.value)}
              placeholder="z.B. Hähnchenbrust, Reis..."
              className="flex-1 px-3 py-2 bg-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500"
              onKeyDown={(e) => e.key === 'Enter' && addManualIngredient()}
            />
            <button
              onClick={addManualIngredient}
              className="px-3 py-2 bg-yellow-500 rounded-lg"
            >
              <Plus className="w-5 h-5 text-black" />
            </button>
          </div>
        </div>

        {/* Added Ingredients */}
        {ingredients.length > 0 && (
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <p className="text-zinc-400 text-xs mb-2">Hinzugefügte Zutaten</p>
            <div className="space-y-2">
              {ingredients.map((ing, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-zinc-700/50 rounded-lg">
                  <span className="text-white text-sm">{ing.name}</span>
                  <button onClick={() => removeIngredient(i)} className="p-1 text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* "Als Rezept speichern" Toggle */}
      <div className="px-4 py-3 border-t border-zinc-800">
        <label className="flex items-center justify-between cursor-pointer select-none">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <span>📑</span>
              Als Rezept speichern
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Mit 1 Klick später erneut loggen
            </div>
          </div>
          <input
            type="checkbox"
            checked={saveAsRecipe}
            onChange={(e) => setSaveAsRecipe(e.target.checked)}
            className="sr-only peer"
          />
          <div className="relative w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:bg-yellow-500 peer-checked:after:translate-x-5 after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
        </label>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-zinc-700 flex gap-2">
        <button
          onClick={resetAndClose}
          className="flex-1 py-3 bg-zinc-700 rounded-xl text-zinc-300 font-medium"
        >
          Abbrechen
        </button>
        <button
          onClick={saveMeal}
          disabled={saving || !mealName.trim() || ingredients.length === 0}
          className="flex-1 py-3 bg-yellow-500 rounded-xl text-black font-bold disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Erstellen'}
        </button>
      </div>
    </div>
  );
};

export default CreateMealModal;
